import { brandManualSocialUrlFromHandle, type BrandManualSocialPlatform } from './brandManualSocialLinkUtils';
import { stripLeadingAt } from './socialHandle';

export type InfluencerProfilePlatform = BrandManualSocialPlatform;

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value.trim());

/** Add https when API returns host/path without a scheme. */
function withHttpsScheme(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    if (isHttpUrl(trimmed)) return trimmed;
    if (/^www\./i.test(trimmed) || /^[\w-]+\.(com|net|org|io)\//i.test(trimmed)) {
        return `https://${trimmed.replace(/^\/+/, '')}`;
    }
    return trimmed;
}

function looksLikeBareHandle(value: string): boolean {
    const v = value.trim();
    return Boolean(v) && !v.includes('/') && !v.includes('.');
}

/** Canonicalize known platform profile URLs (handles, @ prefixes, YouTube paths). */
export function normalizeInfluencerPlatformProfileUrl(platform: InfluencerProfilePlatform, raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) return '';

    if (looksLikeBareHandle(trimmed)) {
        return brandManualSocialUrlFromHandle(platform, trimmed);
    }

    const withScheme = withHttpsScheme(trimmed);
    if (!isHttpUrl(withScheme)) {
        return brandManualSocialUrlFromHandle(platform, trimmed);
    }

    try {
        const parsed = new URL(withScheme);
        const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
        const segments = parsed.pathname.split('/').filter(Boolean);
        const first = segments[0] ?? '';

        if (platform === 'instagram') {
            if (host.includes('instagram.com') && first && !['p', 'reel', 'reels', 'stories'].includes(first)) {
                return `https://www.instagram.com/${stripLeadingAt(first)}`;
            }
        }

        if (platform === 'tiktok') {
            if (host.includes('tiktok.com')) {
                const handle = stripLeadingAt(
                    first.startsWith('@') ? first : segments.find(s => s.startsWith('@')) ?? first,
                );
                if (handle) return `https://www.tiktok.com/@${handle}`;
            }
        }

        if (platform === 'youtube') {
            if (host.includes('youtube.com') || host === 'youtu.be') {
                if (first === 'channel' || first === 'c' || first === 'user') {
                    return parsed.toString().replace(/\/$/, '');
                }
                const handle = stripLeadingAt(first.startsWith('@') ? first : first);
                if (handle) return `https://www.youtube.com/@${handle}`;
            }
        }

        return parsed.toString().replace(/\/$/, '');
    } catch {
        return brandManualSocialUrlFromHandle(platform, trimmed);
    }
}

/** Returns true for asterisk-masked handles like "ace****go" that can't be navigated to. */
function isMaskedHandle(value: string): boolean {
    return value.trim().includes('*');
}

/** Resolve a clickable profile URL from API link and/or username. */
export function resolveInfluencerPlatformProfileUrl(
    platform: InfluencerProfilePlatform,
    stats?: { profile_link?: string | null; username?: string | null },
): string | undefined {
    const link = stats?.profile_link?.trim();
    if (link) {
        const normalized = normalizeInfluencerPlatformProfileUrl(platform, link);
        if (normalized) return normalized;
    }

    const username = stats?.username?.trim();
    if (username && !isMaskedHandle(username)) {
        const built = brandManualSocialUrlFromHandle(platform, username);
        return built || undefined;
    }

    return undefined;
}
